import { useState } from 'react';
import { Mail, MessageCircle, Send, ArrowUpRight, ShieldCheck, Phone } from 'lucide-react';
import { BankPayment } from '../components/BankPayment';
import { toast } from 'sonner';
import { PageHero } from '../components/PageHero';
import { Reveal } from '../components/Reveal';
import { services } from '../data/services';
import { site } from '../data/site';
import { usePageMeta } from '../components/ScrollToTop';

const budgets = ['Prefer not to say', 'Under ₦100,000', '₦100,000 – ₦300,000', '₦300,000 – ₦1,000,000', 'Above ₦1,000,000'];

const inputCls =
  'w-full rounded-xl border border-ink/15 bg-white px-4 py-3.5 text-sm text-ink outline-none transition-colors placeholder:text-ink focus:border-forest';

export default function Contact() {
  usePageMeta(
    'Contact TheBigHub — Let\u2019s Build Your Success Story',
    'Have a business to grow, a brand to build, or a digital project in mind? Contact TheBigHub — Your Digital Growth Partner.'
  );
  const [form, setForm] = useState({
    name: '',
    business: '',
    email: '',
    whatsapp: '',
    service: '',
    budget: '',
    details: '',
    consent: false,
  });

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    if (site.googleFormUrl) {
      window.open(site.googleFormUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    const msg = [
      'Hello TheBigHub, I would like to discuss your digital services.',
      '',
      `Name: ${form.name}`,
      form.business && `Business/Organization: ${form.business}`,
      `Email: ${form.email}`,
      `WhatsApp: ${form.whatsapp}`,
      `Service of interest: ${form.service}`,
      form.budget && `Budget: ${form.budget}`,
      form.details && `Project details: ${form.details}`,
    ]
      .filter(Boolean)
      .join('\n');
    window.open(`https://wa.me/2349138299631?text=${encodeURIComponent(msg)}`, '_blank', 'noopener,noreferrer');
    toast.success('Opening WhatsApp with your project details — press send to reach us instantly.');
  };

  return (
    <main data-testid="contact-page">
      <PageHero
        eyebrow="Contact Us"
        title="Let's Build Your Success Story."
        copy="Have a business to grow, a brand to build, or a digital project in mind? Tell us what you're working on. Let's explore how TheBigHub can help."
      />

      <section className="bg-cream">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
          <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <Reveal>
                <p className="eyebrow mb-5">Reach Us Directly</p>
                <h2 className="font-display text-3xl font-bold text-ink sm:text-4xl">
                  We Reply Like a Partner, Not a Bot.
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-ink sm:text-base">
                  The fastest way to reach us is WhatsApp — tell us what you're working on and
                  we'll take it from there.
                </p>
              </Reveal>

              <div className="mt-10 space-y-4">
                <Reveal delay={0.1}>
                  <a
                    href={site.whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-whatsapp-card"
                    className="card-lift group flex items-center gap-5 rounded-2xl bg-pine p-6 text-white"
                  >
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-goldlight/15 text-goldlight transition-transform duration-300 group-hover:scale-110">
                      <MessageCircle size={22} />
                    </span>
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-goldlight">WhatsApp — Fastest</p>
                      <p className="mt-1 font-display text-xl font-bold">{site.whatsappDisplay}</p>
                    </div>
                    <ArrowUpRight size={20} className="ml-auto text-white transition-colors group-hover:text-goldlight" />
                  </a>
                </Reveal>
                <Reveal delay={0.18}>
                  <a
                    href={`mailto:${site.email}`}
                    data-testid="contact-email-card"
                    className="card-lift group flex items-center gap-5 rounded-2xl border-2 border-forest bg-forest p-6"
                  >
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/15 text-goldlight transition-transform duration-300 group-hover:scale-110">
                      <Mail size={22} />
                    </span>
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-goldlight/80">Email</p>
                      <p className="mt-1 font-display text-xl font-bold text-white">{site.email}</p>
                    </div>
                    <ArrowUpRight size={20} className="ml-auto text-white transition-colors group-hover:text-goldlight" />
                  </a>
                </Reveal>
                <Reveal delay={0.22}>
                  <a
                    href="tel:+2349138299631"
                    data-testid="contact-call-card"
                    className="card-lift group flex items-center gap-5 rounded-2xl border-2 border-forest bg-forest p-6"
                  >
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/15 text-goldlight transition-transform duration-300 group-hover:scale-110">
                      <Phone size={22} />
                    </span>
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-goldlight/80">
                        Call — General Enquiries
                      </p>
                      <p className="mt-1 font-display text-xl font-bold text-white">{site.whatsappDisplay}</p>
                    </div>
                    <ArrowUpRight size={20} className="ml-auto text-white transition-colors group-hover:text-goldlight" />
                  </a>
                </Reveal>
              </div>

              <Reveal delay={0.24}>
                <div className="mt-10 rounded-2xl border-2 border-forest bg-forest p-6">
                  <p className="font-display text-lg font-bold text-white">One Hub. Many Solutions. Maximum Impact.</p>
                  <p className="mt-2 text-sm leading-relaxed text-ink">
                    Whether it's one service or a full growth engine, every conversation starts with
                    understanding your goals.
                  </p>
                </div>
              </Reveal>
              <Reveal delay={0.3}>
                <div className="mt-10">
                  <BankPayment />
                </div>
              </Reveal>
            </div>

            <Reveal delay={0.15}>
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="rounded-2xl border border-ink/10 bg-white p-7 shadow-[0_30px_70px_-34px_rgba(0,59,36,0.35)] sm:p-10"
              >
                <h2 className="font-display text-2xl font-bold text-ink">Tell Us About Your Project</h2>
                <p className="mt-2 text-sm text-ink">
                  Fill this in and we'll open WhatsApp with everything pre-composed — one tap to send.
                </p>

                <div className="mt-8 grid gap-5 sm:grid-cols-2">
                  <label className="block">
                    <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-ink">Full Name *</span>
                    <input required value={form.name} onChange={set('name')} data-testid="contact-input-name" placeholder="Your full name" className={inputCls} />
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-ink">Business / Organization</span>
                    <input value={form.business} onChange={set('business')} data-testid="contact-input-business" placeholder="Company or brand name" className={inputCls} />
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-ink">Email Address *</span>
                    <input required type="email" value={form.email} onChange={set('email')} data-testid="contact-input-email" placeholder="you@example.com" className={inputCls} />
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-ink">WhatsApp Number *</span>
                    <input required type="tel" value={form.whatsapp} onChange={set('whatsapp')} data-testid="contact-input-whatsapp" placeholder="e.g. 0913 829 9631" className={inputCls} />
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-ink">Service of Interest *</span>
                    <select required value={form.service} onChange={set('service')} data-testid="contact-select-service" className={inputCls}>
                      <option value="" disabled>Select a service</option>
                      {services.map((s) => (
                        <option key={s.slug} value={s.title}>{s.title}</option>
                      ))}
                      <option value="Not sure yet">Not sure yet — advise me</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-ink">Project Budget (Optional)</span>
                    <select value={form.budget} onChange={set('budget')} data-testid="contact-select-budget" className={inputCls}>
                      <option value="">Select a range</option>
                      {budgets.map((b) => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                    </select>
                  </label>
                </div>

                <label className="mt-5 block">
                  <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-ink">Project Details *</span>
                  <textarea
                    required
                    rows={4}
                    value={form.details}
                    onChange={set('details')}
                    data-testid="contact-input-details"
                    placeholder="Tell us what you're working on, your goals, and anything else we should know…"
                    className={`${inputCls} resize-none`}
                  />
                </label>

                <label className="mt-5 flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    required
                    checked={form.consent}
                    onChange={set('consent')}
                    data-testid="contact-consent-checkbox"
                    className="mt-1 h-4 w-4 rounded border-ink/30 accent-[#064B2B]"
                  />
                  <span className="text-xs leading-relaxed text-ink">
                    I consent to TheBigHub collecting and using the information I submit to respond
                    to my inquiry, in line with the Privacy Policy. My details will not be shared
                    with third parties.
                  </span>
                </label>

                <button type="submit" data-testid="contact-submit-button" className="btn-forest mt-8 w-full">
                  <Send size={16} /> Send Project Details
                </button>
                <p className="mt-4 flex items-center justify-center gap-2 text-center text-[11px] text-ink">
                  <ShieldCheck size={13} className="text-forest" /> Your information stays private — always.
                </p>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
